import { api } from "../api";
import { ActivityType } from "../../types/enums";

// SCRUM-1514: a second, shorter summary per section. `manual` marks a hand
// edit that automatic refreshes never overwrite; `stale` means the section's
// content changed after the text was generated.
export interface PlanningSuperSummary {
  text: string | null;
  manual: boolean;
  stale: boolean;
}

export type PlanningSuperSummarySections = Record<ActivityType, PlanningSuperSummary>;

export interface PlanningSummarySection {
  text: string | null;
  // Optional because this client and the backend deploy independently.
  superSummary?: PlanningSuperSummary;
}

export type PlanningSummarySections = Record<ActivityType, PlanningSummarySection>;

export interface PlanningSummariesResponse {
  id: number;
  sections: PlanningSummarySections;
}

// Regenerate no longer persists server-side (SCRUM-717 BE-11), so its
// response can no longer carry updated_at/outdated — nothing was written.
export interface PlanningSummarySectionResponse {
  id: number;
  activityType: ActivityType;
  text: string | null;
}

// Unlike the SCRUM-717 summary, the super summary regenerate persists
// server-side, so the returned text is already saved and automatic.
export interface PlanningSuperSummaryResponse {
  id: number;
  activityType: ActivityType;
  text: string | null;
  manual: false;
  stale: false;
}

export interface RegeneratePlanningSummaryInput {
  activityType: ActivityType;
}

const DEFAULT_NO_ACTIVITIES_MESSAGE =
  "Esta sección no tiene actividades vinculadas; no es posible generar un resumen.";
const DEFAULT_GENERATION_FAILED_MESSAGE = "Ocurrió un error al generar el resumen.";

// Thrown on 422: the section has no linked activities, so the server never
// called the model. Callers must show this distinctly from a generation failure.
export class PlanningSummaryNoActivitiesError extends Error {
  constructor(message: string = DEFAULT_NO_ACTIVITIES_MESSAGE) {
    super(message);
    Object.setPrototypeOf(this, PlanningSummaryNoActivitiesError.prototype);
    this.name = "PlanningSummaryNoActivitiesError";
  }
}

// Thrown on 500: generation failed or the model produced an invalid bullet
// count. The previously stored summary is left untouched server-side.
export class PlanningSummaryGenerationFailedError extends Error {
  constructor(message: string = DEFAULT_GENERATION_FAILED_MESSAGE) {
    super(message);
    Object.setPrototypeOf(this, PlanningSummaryGenerationFailedError.prototype);
    this.name = "PlanningSummaryGenerationFailedError";
  }
}

export const getPlanningSummaries = async (
  lessonId: number
): Promise<PlanningSummariesResponse> => {
  const response = await api.get<PlanningSummariesResponse>(
    `/admin/content/lessons/${lessonId}/planning-summaries`
  );
  return response.data;
};

const postRegenerate = async <T>(
  url: string,
  input: RegeneratePlanningSummaryInput
): Promise<T> => {
  try {
    const response = await api.post<T>(url, input);
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    if (status === 422) {
      throw new PlanningSummaryNoActivitiesError(
        typeof message === "string" ? message : undefined
      );
    }

    if (status === 500) {
      throw new PlanningSummaryGenerationFailedError(
        typeof message === "string" ? message : undefined
      );
    }

    throw error;
  }
};

export const regeneratePlanningSummary = (
  lessonId: number,
  input: RegeneratePlanningSummaryInput
): Promise<PlanningSummarySectionResponse> =>
  postRegenerate(
    `/admin/content/lessons/${lessonId}/planning-summary/regenerate`,
    input
  );

export const regeneratePlanningSuperSummary = (
  lessonId: number,
  input: RegeneratePlanningSummaryInput
): Promise<PlanningSuperSummaryResponse> =>
  postRegenerate(
    `/admin/content/lessons/${lessonId}/planning-super-summary/regenerate`,
    input
  );
